import React, { useState, useEffect } from 'react';
import { useApp, User, MedicalRecord, Appointment, getTodayDateString, getSafeUrl, isUserDisabled } from './AppContext';
import { Button } from './Components';
import { LiveCallNotificationBanner } from './LiveCallNotificationBanner';
import { useActiveCallStatus, ParticipantStatusBadge, getParticipantOnlineStatus } from './useActiveCallStatus';
import { PreConsultationTriageModal } from './PreConsultationTriageModal';
import { DigitalPrescriptionModal } from './DigitalPrescriptionModal';
import { MedicalRecordMetadataModal } from './MedicalRecordMetadataModal';

type PatientTabType = 'appointments' | 'vault' | 'doctor-notes' | 'vitals' | 'feedback' | 'security';

// Component: Patient Account Security & Password Management
const PatientSecuritySection: React.FC<{
    user: User | null;
    changeUserPassword: (curr: string, next: string) => Promise<void>;
    resetUserPassword: (email: string) => Promise<void>;
    setPage: (p: string) => void;
}> = ({ user, changeUserPassword, resetUserPassword, setPage }) => {
    const { linkGuestAccount } = useApp();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Guest Account Conversion State
    const [guestName, setGuestName] = useState(user?.name && user.name !== 'Guest Patient' && user.name !== 'Guest User' ? user.name : '');
    const [guestEmail, setGuestEmail] = useState(user?.email && !user.email.includes('guest') ? user.email : '');
    const [guestPassword, setGuestPassword] = useState('');
    const [guestConfirmPassword, setGuestConfirmPassword] = useState('');
    const [showGuestPassword, setShowGuestPassword] = useState(false);
    const [showGuestConfirmPassword, setShowGuestConfirmPassword] = useState(false);
    const [isSavingGuest, setIsSavingGuest] = useState(false);

    const isGuest = Boolean(user?.isAnonymous || !user?.email || user.email.includes('guest') || user.email.includes('demo') || user.email.endsWith('@telemed.local'));

    const handleSaveGuestAccount = async (e: React.FormEvent) => {
        e.preventDefault();
        setFeedback(null);
        if (!guestEmail.trim() || !guestEmail.includes('@')) {
            setFeedback({ type: 'error', text: 'Please enter a valid permanent email address.' });
            return;
        }
        if (guestPassword.length < 6) {
            setFeedback({ type: 'error', text: 'Password must be at least 6 characters long.' });
            return;
        }
        if (guestPassword !== guestConfirmPassword) {
            setFeedback({ type: 'error', text: 'Passwords do not match. Please verify.' });
            return;
        }
        setIsSavingGuest(true);
        try {
            await linkGuestAccount(guestEmail.trim(), guestPassword, guestName.trim());
            setFeedback({
                type: 'success',
                text: 'Your patient account has been saved successfully! Your email and password are now active. You can sign in anytime.'
            });
            setGuestPassword('');
            setGuestConfirmPassword('');
        } catch (err: any) {
            setFeedback({
                type: 'error',
                text: err.message || 'Unable to save account credentials. Please try again.'
            });
        } finally {
            setIsSavingGuest(false);
        }
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setFeedback(null);

        if (isGuest) {
            setFeedback({
                type: 'error',
                text: 'Please use the form below to save your account with your permanent email and password.'
            });
            return;
        }

        if (newPassword.length < 6) {
            setFeedback({
                type: 'error',
                text: 'New password must be at least 6 characters long.'
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            setFeedback({
                type: 'error',
                text: 'New password and confirmation do not match. Please verify.'
            });
            return;
        }

        if (currentPassword && currentPassword === newPassword) {
            setFeedback({
                type: 'error',
                text: 'New password must be different from your current password.'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            await changeUserPassword(currentPassword, newPassword);
            setFeedback({
                type: 'success',
                text: 'Your password has been changed successfully. A security notification was recorded for your account.'
            });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err: any) {
            setFeedback({
                type: 'error',
                text: err.message || 'Unable to update password. Please verify your current credentials and try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendResetEmail = async () => {
        if (!user?.email || isGuest) {
            setFeedback({
                type: 'error',
                text: 'Password reset links can only be dispatched to registered email accounts. Please sign up for a personal account.'
            });
            return;
        }

        setIsSendingEmail(true);
        setFeedback(null);
        try {
            await resetUserPassword(user.email);
            setFeedback({
                type: 'success',
                text: `A secure password reset link has been dispatched to ${user.email}. Please check your email inbox and spam folder.`
            });
        } catch (err: any) {
            setFeedback({
                type: 'error',
                text: err.message || 'Could not send reset email. Please try again later.'
            });
        } finally {
            setIsSendingEmail(false);
        }
    };

    return (
        <div id="security-section" className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-2xs space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-5">
                <div>
                    <h4 className="font-black text-slate-900 text-base sm:text-lg flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-sm shadow-2xs">
                            <i className="fa-solid fa-lock"></i>
                        </span>
                        Account Security & Password Management
                    </h4>
                    <p className="text-xs text-slate-500 font-medium mt-1">
                        Change your login password directly or request a secure email reset link.
                    </p>
                </div>

                <div className="flex items-center gap-2 self-start sm:self-auto">
                    <span className={`text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border ${
                        isGuest 
                            ? 'bg-amber-50 text-amber-700 border-amber-200' 
                            : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    }`}>
                        <i className={`fa-solid ${isGuest ? 'fa-user-clock' : 'fa-shield-check'} mr-1.5`}></i>
                        {isGuest ? 'Temporary Demo' : 'Verified Account'}
                    </span>
                </div>
            </div>

            {feedback && (
                <div 
                    className={`p-4 rounded-2xl text-xs font-bold flex items-start gap-3 animate-in fade-in ${
                        feedback.type === 'success' 
                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                            : 'bg-rose-50 text-rose-800 border border-rose-200'
                    }`}
                    role="alert"
                >
                    <i className={`fa-solid ${feedback.type === 'success' ? 'fa-circle-check text-emerald-600' : 'fa-circle-exclamation text-rose-600'} text-base shrink-0 mt-0.5`}></i>
                    <span className="leading-relaxed">{feedback.text}</span>
                </div>
            )}

            {isGuest ? (
                <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-xs space-y-6">
                    <div className="flex items-start gap-3.5 pb-6 border-b border-slate-100">
                        <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 text-xl shadow-2xs">
                            <i className="fa-solid fa-user-lock"></i>
                        </div>
                        <div>
                            <h4 className="text-base sm:text-lg font-black text-slate-900 tracking-tight">Save Account & Create Your Password</h4>
                            <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">
                                You booked appointments as a guest. Set your email and create a password now so all your consultations, triage notes, prescriptions, and medical records stay safely in your private account.
                            </p>
                        </div>
                    </div>

                    <form onSubmit={handleSaveGuestAccount} className="space-y-4 max-w-xl">
                        <div>
                            <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">
                                Full Name
                            </label>
                            <input
                                type="text"
                                required
                                value={guestName}
                                onChange={(e) => setGuestName(e.target.value)}
                                placeholder="e.g. John Doe"
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">
                                Your Email Address
                            </label>
                            <input
                                type="email"
                                required
                                value={guestEmail}
                                onChange={(e) => setGuestEmail(e.target.value)}
                                placeholder="name@example.com"
                                autoComplete="username"
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                            />
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">
                                    Create Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showGuestPassword ? "text" : "password"}
                                        required
                                        minLength={6}
                                        value={guestPassword}
                                        onChange={(e) => setGuestPassword(e.target.value)}
                                        placeholder="Min. 6 characters"
                                        autoComplete="new-password"
                                        className="w-full px-4 py-3 pr-11 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowGuestPassword(!showGuestPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                                        aria-label={showGuestPassword ? "Hide password" : "Show password"}
                                    >
                                        <i className={`fa-solid ${showGuestPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">
                                    Confirm Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showGuestConfirmPassword ? "text" : "password"}
                                        required
                                        minLength={6}
                                        value={guestConfirmPassword}
                                        onChange={(e) => setGuestConfirmPassword(e.target.value)}
                                        placeholder="Re-type password"
                                        autoComplete="new-password"
                                        className="w-full px-4 py-3 pr-11 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowGuestConfirmPassword(!showGuestConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                                        aria-label={showGuestConfirmPassword ? "Hide password" : "Show password"}
                                    >
                                        <i className={`fa-solid ${showGuestConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="pt-2 flex items-center gap-3">
                            <Button
                                type="submit"
                                disabled={isSavingGuest}
                                className="px-6 py-3.5 text-xs font-black shadow-md"
                            >
                                {isSavingGuest ? (
                                    <span className="flex items-center gap-2">
                                        <i className="fa-solid fa-spinner fa-spin"></i>
                                        Saving Profile...
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        <i className="fa-solid fa-key"></i>
                                        Save Account & Create Password
                                    </span>
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    {/* Direct Change Password Form */}
                    <form onSubmit={handleChangePassword} className="lg:col-span-7 space-y-4">
                        <h5 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2">
                            Update Login Password
                        </h5>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">
                                    Current Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showCurrentPassword ? "text" : "password"}
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        placeholder="Enter your current password"
                                        autoComplete="current-password"
                                        className="w-full px-4 py-3 pr-11 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 transition-colors"
                                        aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                                    >
                                        <i className={`fa-solid ${showCurrentPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showNewPassword ? "text" : "password"}
                                            required
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="Min. 6 characters"
                                            autoComplete="new-password"
                                            className="w-full px-4 py-3 pr-11 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 transition-colors"
                                            aria-label={showNewPassword ? "Hide password" : "Show password"}
                                        >
                                            <i className={`fa-solid ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">
                                        Confirm New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            required
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Re-type new password"
                                            autoComplete="new-password"
                                            className="w-full px-4 py-3 pr-11 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 transition-colors"
                                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                        >
                                            <i className={`fa-solid ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-sm flex items-center gap-2 disabled:opacity-50 active:scale-95"
                            >
                                {isSubmitting ? (
                                    <>
                                        <i className="fa-solid fa-spinner fa-spin"></i>
                                        Updating Password...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa-solid fa-key"></i>
                                        Update Password
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    {/* Email Reset Link Option */}
                    <div className="lg:col-span-5 p-6 bg-slate-50 border border-slate-200/80 rounded-2xl space-y-4">
                        <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center text-lg">
                            <i className="fa-solid fa-envelope-circle-check"></i>
                        </div>
                        <div>
                            <h5 className="text-sm font-black text-slate-900">Email Reset Link</h5>
                            <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">
                                Prefer an email link? We can send an official reset message directly to <strong>{user?.email}</strong>.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleSendResetEmail}
                            disabled={isSendingEmail}
                            className="w-full py-2.5 px-4 bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs rounded-xl border border-slate-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 shadow-2xs"
                        >
                            {isSendingEmail ? (
                                <>
                                    <i className="fa-solid fa-spinner fa-spin"></i>
                                    Sending Link...
                                </>
                            ) : (
                                <>
                                    <i className="fa-solid fa-paper-plane text-indigo-600"></i>
                                    Send Reset Email to {user?.email}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export const PatientDashboard: React.FC<{ onStartConsultation: (id: string) => void; setPage: (p: string) => void }> = ({ onStartConsultation, setPage }) => {
    const { user, appointments, medicalRecords, doctors, cancelAppointment, updateUserProfile, addMedicalRecord, deleteMedicalRecord, uploadClinicalFile, uploadPublicFile, submitReview, changeUserPassword, resetUserPassword, showToast } = useApp();
    const { isCallOngoing, isCallActiveInAnotherTab, isBothConnectedInCall, isCallEnded, callDocStatus } = useActiveCallStatus();

    const [tab, setTab] = useState<PatientTabType>(() => {
        if (typeof window !== 'undefined') {
            const hash = window.location.hash;
            if (hash.includes('tab=doctor-notes') || hash.includes('tab=prescriptions') || hash.includes('doctor-notes') || hash.includes('prescriptions')) return 'doctor-notes';
            if (hash.includes('tab=security') || hash.includes('security') || hash.includes('password')) return 'security';
            if (hash.includes('tab=vitals') || hash.includes('tab=profile') || hash.includes('vitals') || hash.includes('profile')) return 'vitals';
            if (hash.includes('tab=vault') || hash.includes('tab=records') || hash.includes('tab=documents') || hash.includes('vault') || hash.includes('records') || hash.includes('documents')) return 'vault';
            if (hash.includes('tab=feedback') || hash.includes('feedback')) return 'feedback';
            if (hash.includes('tab=appointments') || hash.includes('appointments')) return 'appointments';
        }
        return 'appointments';
    });

    const switchTab = (newTab: PatientTabType, shouldScroll = false) => {
        setTab(newTab);
        if (typeof window !== 'undefined') {
            window.history.pushState({ tab: newTab, section: 'details' }, '', `#tab=${newTab}`);
        }
        if (shouldScroll) {
            setTimeout(() => {
                const el = document.getElementById('patient-tab-content');
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 50);
        }
    };

    const scrollToCards = () => {
        if (typeof window !== 'undefined') {
            window.history.pushState({ section: 'cards' }, '', '#cards');
        }
        const cardsEl = document.getElementById('patient-action-cards');
        if (cardsEl) {
            cardsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Profile Edit State
    const [edit, setEdit] = useState<User>(user || {} as User);
    const [isSyncing, setIsSyncing] = useState(false);
    const [avatarLoading, setAvatarLoading] = useState(false);
    const [copiedMrn, setCopiedMrn] = useState(false);

    // Banner Carousel State
    const [currentBannerSlide, setCurrentBannerSlide] = useState(0);

    // Upload Document State (Patient's Personal Vault)
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadData, setUploadData] = useState({ name: '', notes: '', category: 'Lab Report' });
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [docFilter, setDocFilter] = useState<'All' | 'Lab Report' | 'Imaging / Scan' | 'Discharge Summary' | 'Other'>('All');

    // Modals & Inspection State
    const [viewingDoc, setViewingDoc] = useState<MedicalRecord | null>(null);
    const [viewingPrescriptionRecord, setViewingPrescriptionRecord] = useState<MedicalRecord | null>(null);

    // Pre-Consultation Triage State
    const [triageAppointment, setTriageAppointment] = useState<Appointment | null>(null);

    // Doctor Review State
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [reviewTarget, setReviewTarget] = useState<{ id: string; name: string; specialty?: string } | null>(null);
    const [reviewData, setReviewData] = useState({ rating: 5, comment: '', tags: [] as string[] });

    // Vitals logger quick entry
    const [showVitalsModal, setShowVitalsModal] = useState(false);
    const [vitalsInput, setVitalsInput] = useState({ bp: '', hr: '', sugar: '', weight: '', temp: '' });

    // Sync edit state with user
    useEffect(() => {
        if (user) {
            setEdit(user);
        }
    }, [user]);

    // Handle hash change and browser back/forward popstate events
    useEffect(() => {
        const handleLocationChange = () => {
            if (typeof window !== 'undefined') {
                const hash = window.location.hash;
                if (hash.includes('tab=doctor-notes') || hash.includes('tab=prescriptions')) {
                    setTab('doctor-notes');
                } else if (hash.includes('tab=vitals') || hash.includes('tab=profile')) {
                    setTab('vitals');
                } else if (hash.includes('tab=vault') || hash.includes('tab=records') || hash.includes('tab=documents')) {
                    setTab('vault');
                } else if (hash.includes('tab=feedback')) {
                    setTab('feedback');
                } else if (hash.includes('tab=appointments')) {
                    setTab('appointments');
                } else if (hash === '#cards' || hash === '' || hash === '#') {
                    const cardsEl = document.getElementById('patient-action-cards');
                    if (cardsEl) {
                        cardsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }
                if (hash.includes('action=upload') || hash.includes('upload')) {
                    setShowUploadModal(true);
                }
            }
        };

        const handlePopState = (e: PopStateEvent) => {
            if (e.state?.section === 'cards' || !window.location.hash || window.location.hash === '#cards' || window.location.hash === '') {
                const cardsEl = document.getElementById('patient-action-cards');
                if (cardsEl) {
                    cardsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            } else if (e.state?.tab) {
                setTab(e.state.tab);
                const el = document.getElementById('patient-tab-content');
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            } else {
                handleLocationChange();
            }
        };

        handleLocationChange();
        window.addEventListener('hashchange', handleLocationChange);
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('hashchange', handleLocationChange);
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    // Filter appointments
    const upcoming = appointments.filter(a => a.status === 'upcoming' || a.status === 'in-progress');
    const past = appointments.filter(a => a.status === 'completed');
    const cancelled = appointments.filter(a => a.status === 'cancelled');

    // STRICT SEPARATION:
    // 1. Patient personal records & uploaded files: authoredBy === 'patient' or self-reported
    const patientDocuments = medicalRecords.filter(r => 
        r.patientId === user?.id && (r.authoredBy === 'patient' || r.doctorId === 'self' || (!r.authoredBy && !r.prescriptionItems?.length && !r.rxId))
    );

    // 2. Doctor consultation clinical records & official prescriptions: authoredBy === 'doctor'
    const doctorConsultations = medicalRecords.filter(r => 
        r.patientId === user?.id && (r.authoredBy === 'doctor' || (r.doctorId && r.doctorId !== 'self'))
    );

    // Save profile changes
    const handleSave = async () => {
        setIsSyncing(true);
        try {
            await updateUserProfile(edit);
            showToast("Profile Updated", "Your health profile has been updated successfully.", "success");
        } catch (e) {
            showToast("Save Failed", "We couldn't save your changes. Please check your connection and try again.", "error");
        }
        setIsSyncing(false);
    };

    // Change avatar
    const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setAvatarLoading(true);
            try {
                const result = await uploadPublicFile(file);
                await updateUserProfile({ avatar: result.url });
                setEdit(prev => ({ ...prev, avatar: result.url }));
                showToast("Photo Updated", "Your profile photo has been updated.", "success");
            } catch (err: any) {
                showToast("Upload Failed", "Failed to upload photo. Please check your connection.", "error");
            }
            setAvatarLoading(false);
            e.target.value = '';
        }
    };

    const copyMrn = () => {
        if (user?.mrn) {
            navigator.clipboard.writeText(user.mrn);
            setCopiedMrn(true);
            setTimeout(() => setCopiedMrn(false), 2000);
        }
    };

    const verifyLocation = () => {
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition((pos) => {
                setEdit(prev => ({
                    ...prev,
                    latitude: pos.coords.latitude,
                    longitude: pos.coords.longitude,
                    locationVerifiedAt: new Date().toISOString()
                }));
            });
        }
    };

    // Upload Patient Document
    const handlePatientUpload = async () => {
        if (!uploadData.name) { showToast("Title Required", "Please provide a title for this document.", "error"); return; }
        if (selectedFiles.length === 0) { showToast("File Required", "Please select at least one file to upload.", "error"); return; }
        
        setIsUploading(true);
        try {
            const uploadResults = await Promise.all(selectedFiles.map(f => uploadClinicalFile(f)));
            await addMedicalRecord({
                patientId: user!.id,
                doctorId: 'self',
                doctorName: 'Self-Reported',
                date: getTodayDateString(),
                diagnosis: uploadData.name,
                prescription: uploadData.category,
                notes: uploadData.notes,
                attachmentUrl: uploadResults[0]?.url || '',
                attachmentName: uploadResults[0]?.name || '',
                attachments: uploadResults.map((r, i) => ({
                    name: r.name,
                    url: r.url,
                    size: selectedFiles[i]?.size,
                    type: selectedFiles[i]?.type
                })),
                authoredBy: 'patient'
            });
            setShowUploadModal(false);
            setUploadData({ name: '', notes: '', category: 'Lab Report' });
            setSelectedFiles([]);
            showToast("Document Added", "Your document(s) have been added to your Medical Vault.", "success");
        } catch (e) {
            showToast("Upload Failed", "We couldn't upload your document(s). Please try again.", "error");
        }
        setIsUploading(false);
    };

    // Submit Doctor Review
    const handleSubmitReview = async () => {
        if (!reviewTarget) return;
        try {
            await submitReview(reviewTarget.id, reviewData.rating, reviewData.comment);
            setReviewModalOpen(false);
            setReviewData({ rating: 5, comment: '', tags: [] });
            showToast("Review Submitted", `Thank you! Your feedback for Dr. ${reviewTarget.name} has been submitted.`, "success");
        } catch (e) {
            showToast("Submission Failed", "Failed to submit review.", "error");
        }
    };

    // Banner Slides Data (Accurate schedules & features without fake phone numbers)
    const bannerSlides = [
        {
            title: "ONLINE SPECIALIST DOCTOR CONSULTATIONS",
            tagline: "Scheduled Video Appointments",
            desc: "Consult with verified physicians according to their available schedule. Book your chosen date and time for high-definition video care and official digital prescriptions.",
            featureChip: "Verified Specialist Network",
            featureIcon: "fa-user-doctor",
            badge: "By Appointment",
            actionText: "Find & Book Doctor",
            action: () => setPage('doctors'),
            gradient: "from-rose-600 via-rose-700 to-indigo-800"
        },
        {
            title: "PRE-VISIT CLINICAL HEALTH CHECK-IN",
            tagline: "Prepare For Your Video Visit",
            desc: "Answer a few quick health questions before your scheduled consultation so your doctor can review your current symptoms and health history in advance.",
            featureChip: "Faster Diagnosis & Care",
            featureIcon: "fa-heart-pulse",
            badge: "Pre-Visit Triage",
            actionText: "Start Health Check-in",
            action: () => {
                if (upcoming.length > 0) {
                    setTriageAppointment(upcoming[0]);
                } else {
                    switchTab('appointments', true);
                }
            },
            gradient: "from-indigo-700 via-indigo-800 to-slate-900"
        },
        {
            title: "PERSONAL MEDICAL VAULT & LAB REPORTS",
            tagline: "Encrypted Health Storage",
            desc: "Store, organize, and download your blood tests, radiology scans, and hospital discharge notes securely with full privacy.",
            featureChip: "Zero-Leak Cloud Encryption",
            featureIcon: "fa-shield-halved",
            badge: "Private & Secure",
            actionText: "Upload New Document",
            action: () => setShowUploadModal(true),
            gradient: "from-teal-700 via-emerald-800 to-slate-900"
        }
    ];

    const filteredDocuments = patientDocuments.filter(d => {
        if (docFilter === 'All') return true;
        if (docFilter === 'Lab Report') return d.prescription?.includes('Lab') || d.diagnosis.toLowerCase().includes('lab') || d.diagnosis.toLowerCase().includes('blood');
        if (docFilter === 'Imaging / Scan') return d.prescription?.includes('Imaging') || d.diagnosis.toLowerCase().includes('scan') || d.diagnosis.toLowerCase().includes('mri') || d.diagnosis.toLowerCase().includes('x-ray');
        if (docFilter === 'Discharge Summary') return d.prescription?.includes('Discharge') || d.diagnosis.toLowerCase().includes('discharge') || d.diagnosis.toLowerCase().includes('summary');
        return true;
    });

    return (
        <div className="space-y-10 animate-in fade-in duration-500 pb-16">
            {/* Real-time Call Alert */}
            <LiveCallNotificationBanner onJoinRoom={onStartConsultation} />

            {/* TOP SLIDER BANNER (IDC Red Scheme style from user image) */}
            <div className="relative rounded-3xl overflow-hidden shadow-lg border border-slate-100">
                <div className={`bg-gradient-to-r ${bannerSlides[currentBannerSlide].gradient} text-white p-6 sm:p-8 md:p-10 transition-all duration-700 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative`}>
                    {/* Background subtle geometry */}
                    <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-white/5 skew-x-12 pointer-events-none" />
                    <div className="absolute left-1/4 -bottom-10 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none" />

                    <div className="relative z-10 max-w-2xl">
                        <div className="flex items-center gap-3 mb-2.5">
                            <span className="bg-white/20 backdrop-blur-md text-white text-[11px] font-black uppercase tracking-wider px-3 py-1 rounded-full border border-white/25">
                                {bannerSlides[currentBannerSlide].tagline}
                            </span>
                            <span className="bg-emerald-400 text-slate-900 text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full">
                                {bannerSlides[currentBannerSlide].badge}
                            </span>
                        </div>
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-black tracking-tight leading-snug">
                            {bannerSlides[currentBannerSlide].title}
                        </h2>
                        <p className="text-white/80 text-xs sm:text-sm font-medium mt-2 leading-relaxed">
                            {bannerSlides[currentBannerSlide].desc}
                        </p>

                        <div className="flex flex-wrap items-center gap-4 mt-6">
                            <button
                                onClick={bannerSlides[currentBannerSlide].action}
                                className="px-6 py-3 bg-white text-slate-900 font-black text-xs sm:text-sm uppercase tracking-wider rounded-2xl shadow-md hover:bg-slate-50 active:scale-95 transition-all flex items-center gap-2"
                            >
                                <i className="fa-solid fa-arrow-right"></i>
                                {bannerSlides[currentBannerSlide].actionText}
                            </button>

                            <div className="flex items-center gap-2 text-white/90 text-xs font-bold bg-black/20 backdrop-blur-sm px-3.5 py-2.5 rounded-2xl border border-white/10">
                                <i className={`fa-solid ${bannerSlides[currentBannerSlide].featureIcon} text-rose-300`}></i>
                                <span>{bannerSlides[currentBannerSlide].featureChip}</span>
                            </div>
                        </div>
                    </div>

                    {/* Carousel navigation controls */}
                    <div className="relative z-10 flex md:flex-col items-center gap-3 self-center md:self-auto">
                        <div className="flex gap-1.5">
                            {bannerSlides.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentBannerSlide(idx)}
                                    aria-label={`Slide ${idx + 1}`}
                                    className={`w-3 h-3 rounded-full transition-all ${currentBannerSlide === idx ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/70'}`}
                                />
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setCurrentBannerSlide((prev) => (prev === 0 ? bannerSlides.length - 1 : prev - 1))}
                                aria-label="Previous Slide"
                                className="w-8 h-8 rounded-xl bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-all"
                            >
                                <i className="fa-solid fa-chevron-left text-xs"></i>
                            </button>
                            <button
                                onClick={() => setCurrentBannerSlide((prev) => (prev === bannerSlides.length - 1 ? 0 : prev + 1))}
                                aria-label="Next Slide"
                                className="w-8 h-8 rounded-xl bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-all"
                            >
                                <i className="fa-solid fa-chevron-right text-xs"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* MASTER 6-CARD DASHBOARD GRID (Exact Layout & Visual Balance from User Image) */}
            <section id="patient-action-cards" aria-label="Quick Actions" className="scroll-mt-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg md:text-xl font-black text-slate-900 tracking-tight">Patient Services & Actions</h3>
                        <p className="text-xs text-slate-500 font-medium">Select an option below to view details and manage your healthcare.</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                    {/* CARD 1: View Reports & Documents */}
                    <button
                        onClick={() => switchTab('vault', true)}
                        className={`p-4 sm:p-5 rounded-3xl text-left transition-all duration-300 flex flex-col justify-between relative group border active:scale-98 ${tab === 'vault' ? 'bg-indigo-50/80 border-indigo-300 shadow-md ring-2 ring-indigo-500/20' : 'bg-white border-slate-200/90 shadow-2xs hover:shadow-md hover:border-indigo-200'}`}
                    >
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-wider text-rose-500 bg-rose-50 px-2 py-0.5 rounded-md">
                                {patientDocuments.length} Files
                            </span>
                            <h4 className="text-sm sm:text-base font-black text-slate-900 mt-2 leading-tight">View Reports</h4>
                            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Download & Share</p>
                        </div>
                        <div className="mt-4 pt-3 flex items-center justify-between w-full">
                            <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center text-xl group-hover:scale-110 transition-transform shadow-2xs">
                                <i className="fa-solid fa-folder-open"></i>
                            </div>
                            <span className="w-7 h-7 rounded-full bg-slate-100 group-hover:bg-indigo-600 group-hover:text-white text-slate-400 flex items-center justify-center transition-all">
                                <i className="fa-solid fa-arrow-down text-[10px]"></i>
                            </span>
                        </div>
                    </button>

                    {/* CARD 2: My Bookings & Visits */}
                    <button
                        onClick={() => switchTab('appointments', true)}
                        className={`p-4 sm:p-5 rounded-3xl text-left transition-all duration-300 flex flex-col justify-between relative group border active:scale-98 ${tab === 'appointments' ? 'bg-indigo-50/80 border-indigo-300 shadow-md ring-2 ring-indigo-500/20' : 'bg-white border-slate-200/90 shadow-2xs hover:shadow-md hover:border-indigo-200'}`}
                    >
                        <div>
                            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${upcoming.length > 0 ? 'text-indigo-600 bg-indigo-50' : 'text-slate-500 bg-slate-100'}`}>
                                {upcoming.length > 0 ? `${upcoming.length} Upcoming` : '0 Scheduled'}
                            </span>
                            <h4 className="text-sm sm:text-base font-black text-slate-900 mt-2 leading-tight">My Bookings</h4>
                            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Appointments & Calls</p>
                        </div>
                        <div className="mt-4 pt-3 flex items-center justify-between w-full">
                            <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-xl group-hover:scale-110 transition-transform shadow-2xs">
                                <i className="fa-solid fa-calendar-check"></i>
                            </div>
                            <span className="w-7 h-7 rounded-full bg-slate-100 group-hover:bg-indigo-600 group-hover:text-white text-slate-400 flex items-center justify-center transition-all">
                                <i className="fa-solid fa-arrow-down text-[10px]"></i>
                            </span>
                        </div>
                    </button>

                    {/* CARD 3: Doctor Notes & Prescriptions */}
                    <button
                        onClick={() => switchTab('doctor-notes', true)}
                        className={`p-4 sm:p-5 rounded-3xl text-left transition-all duration-300 flex flex-col justify-between relative group border active:scale-98 ${tab === 'doctor-notes' ? 'bg-indigo-50/80 border-indigo-300 shadow-md ring-2 ring-indigo-500/20' : 'bg-white border-slate-200/90 shadow-2xs hover:shadow-md hover:border-indigo-200'}`}
                    >
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                                {doctorConsultations.length} Visits
                            </span>
                            <h4 className="text-sm sm:text-base font-black text-slate-900 mt-2 leading-tight">Doctor Notes & Rx</h4>
                            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Clinical Prescriptions</p>
                        </div>
                        <div className="mt-4 pt-3 flex items-center justify-between w-full">
                            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl group-hover:scale-110 transition-transform shadow-2xs">
                                <i className="fa-solid fa-prescription"></i>
                            </div>
                            <span className="w-7 h-7 rounded-full bg-slate-100 group-hover:bg-indigo-600 group-hover:text-white text-slate-400 flex items-center justify-center transition-all">
                                <i className="fa-solid fa-arrow-down text-[10px]"></i>
                            </span>
                        </div>
                    </button>

                    {/* CARD 4: Find Specialists & Book */}
                    <button
                        onClick={() => setPage('doctors')}
                        className="p-4 sm:p-5 rounded-3xl text-left transition-all duration-300 flex flex-col justify-between relative group border bg-white border-slate-200/90 shadow-2xs hover:shadow-md hover:border-indigo-200 active:scale-98"
                    >
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-wider text-purple-600 bg-purple-50 px-2 py-0.5 rounded-md">
                                {doctors.length} Specialists
                            </span>
                            <h4 className="text-sm sm:text-base font-black text-slate-900 mt-2 leading-tight">Find Doctors</h4>
                            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Book Specialist</p>
                        </div>
                        <div className="mt-4 pt-3 flex items-center justify-between w-full">
                            <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center text-xl group-hover:scale-110 transition-transform shadow-2xs">
                                <i className="fa-solid fa-user-doctor"></i>
                            </div>
                            <span className="w-7 h-7 rounded-full bg-slate-100 group-hover:bg-indigo-600 group-hover:text-white text-slate-400 flex items-center justify-center transition-all">
                                <i className="fa-solid fa-arrow-right text-[10px]"></i>
                            </span>
                        </div>
                    </button>

                    {/* CARD 5: Profile & Security */}
                    <button
                        onClick={() => switchTab('vitals', true)}
                        className={`p-4 sm:p-5 rounded-3xl text-left transition-all duration-300 flex flex-col justify-between relative group border active:scale-98 ${tab === 'vitals' || tab === 'security' ? 'bg-indigo-50/80 border-indigo-300 shadow-md ring-2 ring-indigo-500/20' : 'bg-white border-slate-200/90 shadow-2xs hover:shadow-md hover:border-indigo-200'}`}
                    >
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-wider text-sky-600 bg-sky-50 px-2 py-0.5 rounded-md">
                                MRN: {user?.mrn || 'Active'}
                            </span>
                            <h4 className="text-sm sm:text-base font-black text-slate-900 mt-2 leading-tight">Profile & Security</h4>
                            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Vitals, Password & ID</p>
                        </div>
                        <div className="mt-4 pt-3 flex items-center justify-between w-full">
                            <div className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center text-xl group-hover:scale-110 transition-transform shadow-2xs">
                                <i className="fa-solid fa-id-card"></i>
                            </div>
                            <span className="w-7 h-7 rounded-full bg-slate-100 group-hover:bg-indigo-600 group-hover:text-white text-slate-400 flex items-center justify-center transition-all">
                                <i className="fa-solid fa-arrow-down text-[10px]"></i>
                            </span>
                        </div>
                    </button>

                    {/* CARD 6: Patient Feedback */}
                    <button
                        onClick={() => switchTab('feedback', true)}
                        className={`p-4 sm:p-5 rounded-3xl text-left transition-all duration-300 flex flex-col justify-between relative group border active:scale-98 ${tab === 'feedback' ? 'bg-indigo-50/80 border-indigo-300 shadow-md ring-2 ring-indigo-500/20' : 'bg-white border-slate-200/90 shadow-2xs hover:shadow-md hover:border-indigo-200'}`}
                    >
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md">
                                Care Ratings
                            </span>
                            <h4 className="text-sm sm:text-base font-black text-slate-900 mt-2 leading-tight">Patient Feedback</h4>
                            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Doctor Reviews</p>
                        </div>
                        <div className="mt-4 pt-3 flex items-center justify-between w-full">
                            <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl group-hover:scale-110 transition-transform shadow-2xs">
                                <i className="fa-solid fa-star"></i>
                            </div>
                            <span className="w-7 h-7 rounded-full bg-slate-100 group-hover:bg-indigo-600 group-hover:text-white text-slate-400 flex items-center justify-center transition-all">
                                <i className="fa-solid fa-arrow-down text-[10px]"></i>
                            </span>
                        </div>
                    </button>
                </div>
            </section>

            {/* TAB CONTAINER WITH SCROLL ANCHOR */}
            <div id="patient-tab-content" className="space-y-4 scroll-mt-6"></div>

            {/* Guest Session Upgrade Banner */}
            {(user?.isAnonymous || !user?.email || user.email.includes('guest')) && (
                <div className="p-4 sm:p-5 bg-gradient-to-r from-indigo-50/90 via-purple-50/80 to-teal-50/70 border border-indigo-100 rounded-3xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-2xs animate-in fade-in">
                    <div className="flex items-center gap-3.5">
                        <div className="w-11 h-11 rounded-2xl bg-indigo-600 text-white flex items-center justify-center text-lg shrink-0 shadow-sm">
                            <i className="fa-solid fa-user-shield"></i>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black uppercase tracking-wider bg-indigo-100/80 text-indigo-700 px-2 py-0.5 rounded-md">
                                    Guest Patient Session
                                </span>
                            </div>
                            <p className="text-xs text-slate-600 font-medium mt-1 leading-snug">
                                Your appointments are active. Create a password to permanently save your medical history and access your account anytime.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => switchTab('security', true)}
                        className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all shrink-0 flex items-center gap-1.5 cursor-pointer active:scale-95"
                    >
                        <i className="fa-solid fa-key"></i>
                        Set Password Now
                    </button>
                </div>
            )}

            {/* TAB CONTENT: 1. APPOINTMENTS & BOOKINGS */}
            {tab === 'appointments' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-xl font-black text-slate-900 tracking-tight">Your Scheduled Consultations</h3>
                            <p className="text-xs text-slate-500 font-medium">Join upcoming video sessions or review completed appointments.</p>
                        </div>
                        <Button onClick={() => setPage('doctors')} className="py-2.5 px-5 text-xs font-black shadow-sm">
                            <i className="fa-solid fa-plus"></i> Book New Consultation
                        </Button>
                    </div>

                    {/* Upcoming Appointments List */}
                    <div className="space-y-4">
                        <h4 className="text-xs font-black uppercase tracking-widest text-slate-400">Upcoming Visits ({upcoming.length})</h4>
                        
                        {upcoming.length === 0 ? (
                            <div className="bg-white border border-slate-200/90 rounded-3xl p-8 sm:p-12 text-center max-w-xl mx-auto shadow-2xs">
                                <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-3xl flex items-center justify-center text-2xl mx-auto mb-4">
                                    <i className="fa-regular fa-calendar-xmark"></i>
                                </div>
                                <h4 className="text-lg font-black text-slate-900">No Upcoming Appointments</h4>
                                <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">
                                    You don't have any consultations scheduled right now. Browse our board-certified specialists to book an instant online session.
                                </p>
                                <button
                                    onClick={() => setPage('doctors')}
                                    className="mt-6 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-2xl transition-all shadow-sm active:scale-95"
                                >
                                    Browse Specialist Directory
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                                {upcoming.map(apt => (
                                    <div key={apt.id} className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-2xs hover:shadow-md transition-all flex flex-col justify-between group">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-14 h-14 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 text-xl font-black shrink-0">
                                                    <i className="fa-solid fa-user-doctor"></i>
                                                </div>
                                                <div>
                                                    {(() => {
                                                        const doctorObj = doctors.find(d => d.id === apt.doctorId) || null;
                                                        return (
                                                            <>
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <h5 className="font-black text-slate-900 text-base leading-tight">{apt.doctorName}</h5>
                                                                    <ParticipantStatusBadge user={doctorObj} callStatus={callDocStatus[apt.id]} isDoctorViewing={false} />
                                                                </div>
                                                                <p className="text-xs font-bold text-indigo-600 mt-0.5">Telemedicine Video Visit</p>
                                                                <p className="text-[11px] text-slate-400 font-medium mt-1">
                                                                    <i className="fa-solid fa-shield-halved text-emerald-500 mr-1"></i> End-to-End Encrypted
                                                                </p>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-end gap-1.5">
                                                {(() => {
                                                    const isCallEndedForApt = isCallEnded(apt.id);
                                                    const isLiveActive = apt.status === 'in-progress' && !isCallEndedForApt;
                                                    return (
                                                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${isLiveActive ? 'bg-rose-100 text-rose-700 animate-pulse' : 'bg-emerald-50 text-emerald-700'}`}>
                                                            {isLiveActive ? 'Call In Progress' : 'Confirmed'}
                                                        </span>
                                                    );
                                                })()}
                                                {apt.reminderSent && (
                                                    <span className="text-[10px] font-bold text-teal-700 bg-teal-50 px-2.5 py-0.5 rounded-full border border-teal-100 flex items-center gap-1 shadow-2xs">
                                                        <i className="fa-solid fa-bell text-[9px]"></i> Reminder Sent
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="bg-slate-50 rounded-2xl p-3.5 my-4 flex items-center justify-between text-xs">
                                            <div className="flex items-center gap-2 text-slate-700 font-bold">
                                                <i className="fa-regular fa-calendar text-indigo-500"></i>
                                                <span>{apt.date}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-slate-700 font-bold">
                                                <i className="fa-regular fa-clock text-indigo-500"></i>
                                                <span>{apt.time}</span>
                                            </div>
                                        </div>

                                        <div className="space-y-2 pt-2 border-t border-slate-100">
                                            {(() => {
                                                const doctorObj = doctors.find(d => d.id === apt.doctorId) || null;
                                                const docStatus = getParticipantOnlineStatus(doctorObj, callDocStatus[apt.id], false);
                                                const isCallEndedForApt = isCallEnded(apt.id);
                                                const isAptCallActive = !isCallEndedForApt && (isCallOngoing(apt.id) || (apt.status === 'in-progress' && isCallActiveInAnotherTab()));
                                                if (isAptCallActive) {
                                                    return (
                                                        <div className="space-y-1.5">
                                                            <div className="w-full py-3 bg-emerald-50 border border-emerald-200 text-emerald-800 font-black text-xs uppercase tracking-wider rounded-2xl flex items-center justify-center gap-2">
                                                                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></span>
                                                                <i className="fa-solid fa-phone-volume text-emerald-600"></i>
                                                                <span>Call In Progress</span>
                                                            </div>
                                                            <div className="text-center">
                                                                <button
                                                                    onClick={() => onStartConsultation(apt.id)}
                                                                    className="text-[11px] font-bold text-slate-400 hover:text-indigo-600 underline transition-colors"
                                                                    title="Switch your ongoing consultation to this window"
                                                                >
                                                                    Switch call to this window
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <button
                                                        onClick={() => onStartConsultation(apt.id)}
                                                        className={`w-full py-3 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all shadow-sm flex items-center justify-center gap-2 active:scale-98 ${
                                                            docStatus.isInRoom
                                                                ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20 ring-2 ring-emerald-400/50'
                                                                : 'bg-indigo-600 hover:bg-indigo-700'
                                                        }`}
                                                    >
                                                        <i className="fa-solid fa-video"></i>
                                                        {docStatus.isInRoom 
                                                            ? 'Doctor in Room • Join Now' 
                                                            : docStatus.isOnline 
                                                                ? 'Doctor Online • Join Consultation' 
                                                                : 'Join Video Consultation'}
                                                    </button>
                                                );
                                            })()}

                                            <div className="grid grid-cols-2 gap-2">
                                                <button
                                                    onClick={() => setTriageAppointment(apt)}
                                                    className="py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-1.5"
                                                >
                                                    <i className="fa-solid fa-clipboard-check"></i>
                                                    Pre-Visit Check-In
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (confirm("Are you sure you want to cancel this consultation?")) {
                                                            cancelAppointment(apt.id, "Cancelled by patient");
                                                        }
                                                    }}
                                                    className="py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-600 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-1.5"
                                                >
                                                    <i className="fa-solid fa-xmark"></i>
                                                    Cancel Visit
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Past Consultations List */}
                    {past.length > 0 && (
                        <div className="space-y-4 pt-6 border-t border-slate-200">
                            <h4 className="text-xs font-black uppercase tracking-widest text-slate-400">Past Completed Visits ({past.length})</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {past.map(apt => (
                                    <div key={apt.id} className="bg-white border border-slate-200/90 rounded-3xl p-5 shadow-2xs hover:shadow-sm transition-all flex flex-col justify-between">
                                        <div>
                                            <div className="flex items-start justify-between">
                                                <h5 className="font-bold text-slate-900 text-sm">{apt.doctorName}</h5>
                                                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                                                    Completed
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 font-medium mt-1">
                                                <i className="fa-regular fa-calendar mr-1"></i> {apt.date} @ {apt.time}
                                            </p>
                                        </div>

                                        <div className="mt-4 pt-3 border-t border-slate-100 flex gap-2">
                                            <button
                                                onClick={() => {
                                                    setReviewTarget({ id: apt.doctorId, name: apt.doctorName });
                                                    setReviewModalOpen(true);
                                                }}
                                                className="flex-1 py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-1"
                                            >
                                                <i className="fa-solid fa-star text-amber-500"></i>
                                                Rate Doctor
                                            </button>
                                            <button
                                                onClick={() => switchTab('doctor-notes')}
                                                className="flex-1 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-1"
                                            >
                                                <i className="fa-solid fa-file-prescription text-indigo-500"></i>
                                                View Notes
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* TAB CONTENT: 2. MY REPORTS & DOCUMENTS (STRICTLY PATIENT FILES) */}
            {tab === 'vault' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-xl font-black text-slate-900 tracking-tight">Personal Medical Reports & Documents</h3>
                            <p className="text-xs text-slate-500 font-medium">Your uploaded lab test results, imaging scans, blood work, and hospital files.</p>
                        </div>
                        <button
                            onClick={() => setShowUploadModal(true)}
                            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all shadow-sm flex items-center gap-2 self-start sm:self-auto active:scale-95"
                        >
                            <i className="fa-solid fa-cloud-arrow-up"></i>
                            Upload New Document
                        </button>
                    </div>

                    {/* Filter Pills */}
                    <div className="flex items-center gap-2 overflow-x-auto pb-1">
                        {(['All', 'Lab Report', 'Imaging / Scan', 'Discharge Summary', 'Other'] as const).map(cat => (
                            <button
                                key={cat}
                                onClick={() => setDocFilter(cat)}
                                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${docFilter === cat ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>

                    {filteredDocuments.length === 0 ? (
                        <div className="bg-white border border-slate-200/90 rounded-3xl p-8 sm:p-12 text-center max-w-xl mx-auto shadow-2xs">
                            <div className="w-16 h-16 bg-rose-50 text-rose-600 rounded-3xl flex items-center justify-center text-2xl mx-auto mb-4">
                                <i className="fa-solid fa-file-arrow-up"></i>
                            </div>
                            <h4 className="text-lg font-black text-slate-900">No Medical Documents Uploaded</h4>
                            <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">
                                Upload your past lab test results, X-rays, blood reports, or discharge summaries so you always have them readily available.
                            </p>
                            <button
                                onClick={() => setShowUploadModal(true)}
                                className="mt-6 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-2xl transition-all shadow-sm active:scale-95"
                            >
                                Upload Your First Report
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            {filteredDocuments.map(doc => (
                                <div key={doc.id} className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-2xs hover:shadow-md transition-all flex flex-col justify-between group">
                                    <div>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center text-xl shrink-0 group-hover:scale-105 transition-transform">
                                                <i className="fa-solid fa-file-pdf"></i>
                                            </div>
                                            <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                                                {doc.prescription || 'Document'}
                                            </span>
                                        </div>

                                        <h4 className="font-black text-slate-900 text-base mt-4 leading-tight line-clamp-2">
                                            {doc.diagnosis}
                                        </h4>
                                        <div className="flex items-center justify-between gap-2 mt-1">
                                            <p className="text-[11px] text-slate-400 font-bold">
                                                <i className="fa-regular fa-clock mr-1"></i> Uploaded on {doc.date}
                                            </p>
                                            {doc.attachments && doc.attachments.length > 1 && (
                                                <span className="text-[10px] font-black text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0">
                                                    <i className="fa-solid fa-paperclip"></i> {doc.attachments.length} files
                                                </span>
                                            )}
                                        </div>

                                        {doc.notes && (
                                            <p className="text-xs text-slate-600 font-medium mt-3 bg-slate-50 p-3 rounded-xl line-clamp-2 leading-relaxed">
                                                "{doc.notes}"
                                            </p>
                                        )}
                                    </div>

                                    <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-2">
                                        <button
                                            onClick={() => setViewingDoc(doc)}
                                            className="flex-1 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-1.5"
                                        >
                                            <i className="fa-solid fa-folder-open"></i>
                                            {doc.attachments && doc.attachments.length > 1 ? `View Files (${doc.attachments.length})` : (doc.attachmentUrl ? 'Open File / Details' : 'View Details')}
                                        </button>
                                        <button
                                            onClick={() => setViewingDoc(doc)}
                                            className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-all shadow-2xs"
                                            title="View Metadata & File Details"
                                            aria-label="View Metadata & File Details"
                                        >
                                            <i className="fa-solid fa-circle-info text-xs text-indigo-600"></i>
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (window.confirm(`Are you sure you want to delete "${doc.diagnosis}"? This report will be permanently removed.`)) {
                                                    deleteMedicalRecord(doc.id);
                                                }
                                            }}
                                            className="w-10 h-10 rounded-xl bg-rose-50 hover:bg-rose-600 text-rose-500 hover:text-white flex items-center justify-center transition-all shadow-2xs"
                                            title="Delete Medical Report"
                                            aria-label="Delete Medical Report"
                                        >
                                            <i className="fa-solid fa-trash-can text-xs"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* TAB CONTENT: 3. DOCTOR CONSULTATION NOTES & PRESCRIPTIONS */}
            {tab === 'doctor-notes' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-xl font-black text-slate-900 tracking-tight">Doctor Consultation Notes & Prescriptions</h3>
                            <p className="text-xs text-slate-500 font-medium">Official clinical summaries, diagnoses, and digital prescriptions provided by your doctors.</p>
                        </div>
                        <Button onClick={() => setPage('doctors')} variant="outline" className="py-2.5 px-4 text-xs font-bold">
                            <i className="fa-solid fa-calendar-plus"></i> Book Follow-Up Visit
                        </Button>
                    </div>

                    {doctorConsultations.length === 0 ? (
                        <div className="bg-white border border-slate-200/90 rounded-3xl p-8 sm:p-12 text-center max-w-xl mx-auto shadow-2xs">
                            <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-3xl flex items-center justify-center text-2xl mx-auto mb-4">
                                <i className="fa-solid fa-notes-medical"></i>
                            </div>
                            <h4 className="text-lg font-black text-slate-900">No Doctor Consultation Notes Yet</h4>
                            <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">
                                When you complete an online video consultation, your doctor's clinical findings, advice, and digital prescriptions will be recorded here automatically.
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {doctorConsultations.map(doc => (
                                <div key={doc.id} className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-2xs hover:shadow-md transition-all flex flex-col justify-between">
                                    <div>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl font-black shrink-0">
                                                    <i className="fa-solid fa-user-md"></i>
                                                </div>
                                                <div>
                                                    <h4 className="font-black text-slate-900 text-base leading-tight">
                                                        {doc.doctorName.toLowerCase().startsWith('dr') ? doc.doctorName : `Dr. ${doc.doctorName}`}
                                                    </h4>
                                                    <p className="text-[11px] font-bold text-emerald-600">Telemedicine Consultation</p>
                                                </div>
                                            </div>
                                            <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                                                Verified e-Rx
                                            </span>
                                        </div>

                                        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                                            <div>
                                                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Clinical Diagnosis</p>
                                                <p className="text-sm font-bold text-slate-900 mt-0.5">{doc.diagnosis}</p>
                                            </div>

                                            {doc.prescription && doc.prescription !== 'N/A' && (
                                                <div className="bg-emerald-50/70 border border-emerald-100 rounded-2xl p-3.5">
                                                    <p className="text-[10px] font-black uppercase tracking-wider text-emerald-800 flex items-center gap-1.5">
                                                        <i className="fa-solid fa-pills text-emerald-600"></i> Official Prescription (e-Rx)
                                                    </p>
                                                    <p className="text-xs font-bold text-emerald-950 mt-1">{doc.prescription}</p>
                                                </div>
                                            )}

                                            {doc.notes && (
                                                <div>
                                                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Doctor Advice & Follow-Up</p>
                                                    <p className="text-xs text-slate-600 font-medium mt-0.5 leading-relaxed bg-slate-50 p-3 rounded-xl">
                                                        {doc.notes}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                                        <span className="text-[11px] text-slate-400 font-bold">
                                            <i className="fa-regular fa-calendar mr-1"></i> {doc.date}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setViewingDoc(doc)}
                                                className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all flex items-center gap-1.5 active:scale-95 shadow-2xs"
                                                title="View Metadata & Clinical Details"
                                            >
                                                <i className="fa-solid fa-circle-info text-indigo-600"></i>
                                                View Metadata
                                            </button>
                                            <button
                                                onClick={() => setViewingPrescriptionRecord(doc)}
                                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl transition-all shadow-sm flex items-center gap-1.5 active:scale-95"
                                            >
                                                <i className="fa-solid fa-receipt"></i>
                                                View Official e-Prescription
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* TAB CONTENT: 4. HEALTH PROFILE & ID (STRICTLY PROFILE & VITALS) */}
            {tab === 'vitals' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-xl font-black text-slate-900 tracking-tight">Health Profile, Vitals & Medical History</h3>
                            <p className="text-xs text-slate-500 font-medium">Add and update your biometrics, recorded vitals, medical background, and digital ID card.</p>
                        </div>
                        <div className="flex items-center gap-2.5 flex-wrap self-start sm:self-auto">
                            <button
                                type="button"
                                onClick={() => {
                                    const el = document.getElementById('security-section');
                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-2xl transition-all flex items-center gap-1.5 active:scale-95 shadow-2xs"
                            >
                                <i className="fa-solid fa-shield-halved text-indigo-600"></i>
                                Password & Security
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSyncing}
                                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all shadow-sm flex items-center gap-2 disabled:opacity-50 active:scale-95"
                            >
                                <i className="fa-solid fa-floppy-disk"></i>
                                {isSyncing ? "Saving Changes..." : "Save Health Profile"}
                            </button>
                        </div>
                    </div>

                    {/* SECTION A: BIOMETRIC VITALS & MEASUREMENTS */}
                    <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-2xs space-y-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-4">
                            <div>
                                <h4 className="font-black text-slate-900 text-base flex items-center gap-2">
                                    <span className="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center text-sm">
                                        <i className="fa-solid fa-heart-pulse"></i>
                                    </span>
                                    Physical Vitals & Body Measurements
                                </h4>
                                <p className="text-xs text-slate-500 font-medium">Keep your vital signs up to date for accurate clinical consultations.</p>
                            </div>

                            {/* Dynamic BMI Summary Badge */}
                            {(() => {
                                const hNum = parseFloat((edit.height || '').replace(/[^0-9.]/g, ''));
                                const wNum = parseFloat((edit.weight || '').replace(/[^0-9.]/g, ''));
                                if (hNum > 0 && wNum > 0) {
                                    // Height in cm to meters
                                    const hM = hNum > 3 ? hNum / 100 : hNum * 0.3048; // if in cm or ft
                                    const bmi = (wNum / (hM * hM)).toFixed(1);
                                    const bmiVal = parseFloat(bmi);
                                    let status = "Healthy Weight";
                                    let color = "bg-emerald-50 text-emerald-700 border-emerald-200";
                                    if (bmiVal < 18.5) { status = "Underweight"; color = "bg-sky-50 text-sky-700 border-sky-200"; }
                                    else if (bmiVal >= 25 && bmiVal < 30) { status = "Overweight"; color = "bg-amber-50 text-amber-700 border-amber-200"; }
                                    else if (bmiVal >= 30) { status = "Obese"; color = "bg-rose-50 text-rose-700 border-rose-200"; }
                                    return (
                                        <div className={`px-3 py-1.5 rounded-xl border text-xs font-bold flex items-center gap-2 self-start sm:self-auto ${color}`}>
                                            <i className="fa-solid fa-calculator"></i>
                                            <span>Calculated BMI: <strong>{bmi}</strong> ({status})</span>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Height */}
                            <div className="bg-slate-50/80 p-4 rounded-2xl border border-slate-100">
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                                    <i className="fa-solid fa-ruler-vertical text-indigo-500 mr-1.5"></i> Height
                                </label>
                                <input
                                    type="text"
                                    value={edit.height || ''}
                                    onChange={(e) => setEdit({ ...edit, height: e.target.value })}
                                    placeholder="e.g. 175 cm or 5'9&quot;"
                                    className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                                <span className="text-[10px] text-slate-400 mt-1 block">In centimeters (cm) or feet/inches</span>
                            </div>

                            {/* Weight */}
                            <div className="bg-slate-50/80 p-4 rounded-2xl border border-slate-100">
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                                    <i className="fa-solid fa-weight-scale text-indigo-500 mr-1.5"></i> Body Weight
                                </label>
                                <input
                                    type="text"
                                    value={edit.weight || ''}
                                    onChange={(e) => setEdit({ ...edit, weight: e.target.value })}
                                    placeholder="e.g. 70 kg or 154 lbs"
                                    className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                                <span className="text-[10px] text-slate-400 mt-1 block">In kilograms (kg) or pounds (lbs)</span>
                            </div>

                            {/* Blood Pressure */}
                            <div className="bg-slate-50/80 p-4 rounded-2xl border border-slate-100">
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                                    <i className="fa-solid fa-stethoscope text-rose-500 mr-1.5"></i> Blood Pressure (BP)
                                </label>
                                <input
                                    type="text"
                                    value={edit.lastBP || ''}
                                    onChange={(e) => setEdit({ ...edit, lastBP: e.target.value })}
                                    placeholder="e.g. 120/80 mmHg"
                                    className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                                <span className="text-[10px] text-slate-400 mt-1 block">Systolic / Diastolic pressure</span>
                            </div>

                            {/* Resting Heart Rate */}
                            <div className="bg-slate-50/80 p-4 rounded-2xl border border-slate-100">
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                                    <i className="fa-solid fa-heart text-rose-500 mr-1.5"></i> Resting Heart Rate (Pulse)
                                </label>
                                <input
                                    type="text"
                                    value={edit.lastHeartRate || ''}
                                    onChange={(e) => setEdit({ ...edit, lastHeartRate: e.target.value })}
                                    placeholder="e.g. 72 bpm"
                                    className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                                <span className="text-[10px] text-slate-400 mt-1 block">Beats per minute (BPM)</span>
                            </div>

                            {/* Blood Glucose */}
                            <div className="bg-slate-50/80 p-4 rounded-2xl border border-slate-100">
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                                    <i className="fa-solid fa-droplet text-amber-500 mr-1.5"></i> Blood Sugar / Glucose
                                </label>
                                <input
                                    type="text"
                                    value={edit.lastGlucose || ''}
                                    onChange={(e) => setEdit({ ...edit, lastGlucose: e.target.value })}
                                    placeholder="e.g. 95 mg/dL (Fasting)"
                                    className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                                <span className="text-[10px] text-slate-400 mt-1 block">Fasting or Post-prandial level</span>
                            </div>

                            {/* Body Temperature */}
                            <div className="bg-slate-50/80 p-4 rounded-2xl border border-slate-100">
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                                    <i className="fa-solid fa-temperature-half text-amber-500 mr-1.5"></i> Body Temperature
                                </label>
                                <input
                                    type="text"
                                    value={edit.lastTemp || ''}
                                    onChange={(e) => setEdit({ ...edit, lastTemp: e.target.value })}
                                    placeholder="e.g. 98.6 °F or 37 °C"
                                    className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                                <span className="text-[10px] text-slate-400 mt-1 block">Normal resting oral / axillary temp</span>
                            </div>
                        </div>
                    </div>

                    {/* SECTION B: CLINICAL HISTORY, ALLERGIES & MEDICATIONS */}
                    <div className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-2xs space-y-6">
                        <div className="border-b border-slate-100 pb-4">
                            <h4 className="font-black text-slate-900 text-base flex items-center gap-2">
                                <span className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-sm">
                                    <i className="fa-solid fa-notes-medical"></i>
                                </span>
                                Medical Background & Allergies
                            </h4>
                            <p className="text-xs text-slate-500 font-medium">Record any drug allergies, ongoing medical conditions, or daily medications.</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            {/* Known Allergies */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-[11px] font-black uppercase tracking-wider text-slate-600 block">
                                        Known Drug & Food Allergies
                                    </label>
                                    <span className="text-[10px] font-bold text-rose-500">Important for Rx</span>
                                </div>
                                <textarea
                                    rows={2}
                                    value={edit.allergies || ''}
                                    onChange={(e) => setEdit({ ...edit, allergies: e.target.value })}
                                    placeholder="e.g. Penicillin, Sulfa drugs, Peanuts, Latex, None"
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                />
                                <div className="flex flex-wrap gap-1.5">
                                    {['No Known Allergies', 'Penicillin', 'Sulfa Drugs', 'Aspirin/NSAIDs', 'Latex'].map((tag) => (
                                        <button
                                            key={tag}
                                            type="button"
                                            onClick={() => {
                                                const curr = edit.allergies || '';
                                                if (tag === 'No Known Allergies') {
                                                    setEdit({ ...edit, allergies: 'No Known Allergies' });
                                                } else if (!curr.includes(tag)) {
                                                    setEdit({ ...edit, allergies: curr ? `${curr}, ${tag}` : tag });
                                                }
                                            }}
                                            className="px-2 py-0.5 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 text-slate-600 rounded-lg text-[10px] font-bold transition-all border border-slate-200/70"
                                        >
                                            + {tag}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Past Medical History & Chronic Illnesses */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-[11px] font-black uppercase tracking-wider text-slate-600 block">
                                        Chronic Conditions / Medical History
                                    </label>
                                </div>
                                <textarea
                                    rows={2}
                                    value={edit.pastMedicalHistory || ''}
                                    onChange={(e) => setEdit({ ...edit, pastMedicalHistory: e.target.value })}
                                    placeholder="e.g. Hypertension, Asthma, Type 2 Diabetes, Thyroid, None"
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                />
                                <div className="flex flex-wrap gap-1.5">
                                    {['None', 'Hypertension', 'Diabetes (Type 2)', 'Asthma', 'Thyroid Disorder'].map((cond) => (
                                        <button
                                            key={cond}
                                            type="button"
                                            onClick={() => {
                                                const curr = edit.pastMedicalHistory || '';
                                                if (cond === 'None') {
                                                    setEdit({ ...edit, pastMedicalHistory: 'None' });
                                                } else if (!curr.includes(cond)) {
                                                    setEdit({ ...edit, pastMedicalHistory: curr ? `${curr}, ${cond}` : cond });
                                                }
                                            }}
                                            className="px-2 py-0.5 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 text-slate-600 rounded-lg text-[10px] font-bold transition-all border border-slate-200/70"
                                        >
                                            + {cond}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Current Daily Medications */}
                            <div className="space-y-2">
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-600 block">
                                    Current Daily Medications & Supplements
                                </label>
                                <textarea
                                    rows={2}
                                    value={edit.currentMedications || ''}
                                    onChange={(e) => setEdit({ ...edit, currentMedications: e.target.value })}
                                    placeholder="e.g. Metformin 500mg (Daily), Lisinopril 10mg, Vitamin D"
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                />
                            </div>

                            {/* Past Surgeries & Social History */}
                            <div className="space-y-2">
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-600 block">
                                    Surgical History & Lifestyle Notes
                                </label>
                                <textarea
                                    rows={2}
                                    value={edit.socialHistory || ''}
                                    onChange={(e) => setEdit({ ...edit, socialHistory: e.target.value })}
                                    placeholder="e.g. Appendectomy (2018), Non-smoker, Regular exercise"
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                />
                            </div>
                        </div>
                    </div>

                    {/* SECTION C: DIGITAL HEALTH ID & PERSONAL PROFILE */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* DIGITAL HEALTH ID CARD */}
                        <div className="lg:col-span-1 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white rounded-3xl p-6 shadow-xl relative overflow-hidden flex flex-col justify-between">
                            <div className="absolute right-0 top-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl pointer-events-none" />
                            
                            <div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center font-black text-xs">
                                            R
                                        </div>
                                        <span className="font-black text-sm tracking-wider">ROVITA HEALTH ID</span>
                                    </div>
                                    <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-md">
                                        Active
                                    </span>
                                </div>

                                <div className="mt-8 flex items-center gap-4">
                                    <div className="relative">
                                        <img
                                            src={edit.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=256'}
                                            className="w-16 h-16 rounded-2xl object-cover border-2 border-white/20 shadow-md"
                                            alt={edit.name}
                                        />
                                        <label className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px] cursor-pointer hover:bg-indigo-500 transition-all shadow-sm">
                                            <i className="fa-solid fa-camera"></i>
                                            <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
                                        </label>
                                    </div>
                                    <div>
                                        <h4 className="font-black text-lg text-white leading-tight">{edit.name || 'Patient'}</h4>
                                        <p className="text-xs text-slate-400 font-medium">{edit.email}</p>
                                        <p className="text-[10px] text-indigo-400 font-bold uppercase tracking-wider mt-0.5">
                                            Blood Group: {edit.bloodType || 'O+'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 pt-4 border-t border-white/10 space-y-3">
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Medical Record Number (MRN)</p>
                                    <div className="flex items-center justify-between mt-1 bg-white/5 p-2.5 rounded-xl border border-white/10">
                                        <span className="font-mono text-sm font-bold tracking-wider">{edit.mrn || 'RV-8942-PAT'}</span>
                                        <button
                                            onClick={copyMrn}
                                            className="text-xs text-indigo-300 hover:text-white transition-colors"
                                            title="Copy MRN"
                                        >
                                            <i className={`fa-solid ${copiedMrn ? 'fa-check text-emerald-400' : 'fa-copy'}`}></i>
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="bg-white/5 p-2 rounded-xl">
                                        <span className="text-[9px] text-slate-400 block font-bold">GENDER</span>
                                        <span className="font-bold">{edit.gender || 'Not specified'}</span>
                                    </div>
                                    <div className="bg-white/5 p-2 rounded-xl">
                                        <span className="text-[9px] text-slate-400 block font-bold">DOB</span>
                                        <span className="font-bold">{edit.dob || '1992-05-14'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* PERSONAL & CONTACT INFORMATION FORM */}
                        <div className="lg:col-span-2 bg-white border border-slate-200/90 rounded-3xl p-6 shadow-2xs space-y-5">
                            <h4 className="font-black text-slate-900 text-base">Personal & Contact Details</h4>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[11px] font-black uppercase tracking-wider text-slate-400 block mb-1">Full Legal Name</label>
                                    <input
                                        type="text"
                                        value={edit.name || ''}
                                        onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>

                                <div>
                                    <label className="text-[11px] font-black uppercase tracking-wider text-slate-400 block mb-1">Phone Number</label>
                                    <input
                                        type="tel"
                                        value={edit.phone || ''}
                                        onChange={(e) => setEdit({ ...edit, phone: e.target.value })}
                                        placeholder="+1 (555) 000-0000"
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>

                                <div>
                                    <label className="text-[11px] font-black uppercase tracking-wider text-slate-400 block mb-1">Date of Birth</label>
                                    <input
                                        type="date"
                                        value={edit.dob || ''}
                                        onChange={(e) => setEdit({ ...edit, dob: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>

                                <div>
                                    <label className="text-[11px] font-black uppercase tracking-wider text-slate-400 block mb-1">Blood Group</label>
                                    <select
                                        value={edit.bloodType || 'O+'}
                                        onChange={(e) => setEdit({ ...edit, bloodType: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="A+">A+</option>
                                        <option value="A-">A-</option>
                                        <option value="B+">B+</option>
                                        <option value="B-">B-</option>
                                        <option value="AB+">AB+</option>
                                        <option value="AB-">AB-</option>
                                        <option value="O+">O+</option>
                                        <option value="O-">O-</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-400 block mb-1">Residential Address</label>
                                <input
                                    type="text"
                                    value={edit.address || ''}
                                    onChange={(e) => setEdit({ ...edit, address: e.target.value })}
                                    placeholder="Street Address, City, State, ZIP"
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div className="pt-2 border-t border-slate-100">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-[11px] font-black uppercase tracking-wider text-slate-400">Emergency Contact</label>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <input
                                        type="text"
                                        value={edit.emergencyContactName || ''}
                                        onChange={(e) => setEdit({ ...edit, emergencyContactName: e.target.value })}
                                        placeholder="Contact Name (e.g. Spouse / Parent)"
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                    <input
                                        type="tel"
                                        value={edit.emergencyContactPhone || ''}
                                        onChange={(e) => setEdit({ ...edit, emergencyContactPhone: e.target.value })}
                                        placeholder="Emergency Phone Number"
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>

                            <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-bold text-slate-900">Location Verification</p>
                                    <p className="text-[11px] text-slate-500">
                                        {edit.locationVerifiedAt ? `Verified: ${new Date(edit.locationVerifiedAt).toLocaleDateString()}` : 'Not verified yet'}
                                    </p>
                                </div>
                                <button
                                    onClick={verifyLocation}
                                    type="button"
                                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all flex items-center gap-1.5"
                                >
                                    <i className="fa-solid fa-location-crosshairs text-indigo-600"></i>
                                    Detect GPS Location
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* SECTION D: ACCOUNT SECURITY & CREDENTIALS */}
                    <PatientSecuritySection 
                        user={user} 
                        changeUserPassword={changeUserPassword} 
                        resetUserPassword={resetUserPassword} 
                        setPage={setPage} 
                    />
                </div>
            )}

            {/* TAB CONTENT: 5. PATIENT FEEDBACK */}
            {tab === 'feedback' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-xl font-black text-slate-900 tracking-tight">Patient Care Reviews & Feedback</h3>
                            <p className="text-xs text-slate-500 font-medium">Rate your experience with your doctors and help us maintain top quality care.</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {doctors.filter(d => !isUserDisabled(d)).slice(0, 4).map(doc => (
                            <div key={doc.id} className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-2xs hover:shadow-md transition-all flex items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <img src={doc.avatar} className="w-14 h-14 rounded-2xl object-cover bg-indigo-50" alt={doc.name} />
                                    <div>
                                        <h4 className="font-black text-slate-900 text-base">{doc.name}</h4>
                                        <p className="text-xs font-bold text-indigo-600">{(doc as any).specialty || doc.specialties?.join(', ') || 'Specialist'}</p>
                                        <div className="flex items-center gap-1 text-amber-500 text-xs mt-1">
                                            <i className="fa-solid fa-star"></i>
                                            <span className="font-bold text-slate-700">{(doc as any).rating || 4.9}</span>
                                            <span className="text-slate-400 font-medium">({(doc as any).reviewCount || 42} reviews)</span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setReviewTarget({ id: doc.id, name: doc.name, specialty: (doc as any).specialty || doc.specialties?.[0] || 'Specialist' });
                                        setReviewModalOpen(true);
                                    }}
                                    className="px-4 py-2.5 bg-amber-50 hover:bg-amber-100 text-amber-700 font-bold text-xs rounded-xl transition-all shadow-2xs shrink-0 flex items-center gap-1.5 active:scale-95"
                                >
                                    <i className="fa-solid fa-star text-amber-500"></i>
                                    Write Review
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* TAB CONTENT: 6. SECURITY & CREDENTIALS */}
            {tab === 'security' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-xl font-black text-slate-900 tracking-tight">Security & Credentials</h3>
                            <p className="text-xs text-slate-500 font-medium">Manage your patient account credentials, update your password, or request reset links.</p>
                        </div>
                        <button
                            onClick={() => switchTab('vitals', true)}
                            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all flex items-center gap-2 self-start sm:self-auto shadow-2xs"
                        >
                            <i className="fa-solid fa-arrow-left text-xs"></i>
                            Back to Profile
                        </button>
                    </div>

                    <PatientSecuritySection 
                        user={user} 
                        changeUserPassword={changeUserPassword} 
                        resetUserPassword={resetUserPassword} 
                        setPage={setPage} 
                    />
                </div>
            )}

            {/* UPLOAD DOCUMENT MODAL (Patient Vault) */}
            {showUploadModal && (
                <div className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-slate-100 animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-lg">
                                    <i className="fa-solid fa-cloud-arrow-up"></i>
                                </div>
                                <div>
                                    <h4 className="font-black text-slate-900 text-lg">Upload Medical Document</h4>
                                    <p className="text-xs text-slate-400 font-medium">Personal files, lab reports, or scans</p>
                                </div>
                            </div>
                            <button onClick={() => setShowUploadModal(false)} className="text-slate-400 hover:text-slate-600 text-xl">
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <div className="space-y-4 mt-6">
                            <div>
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-1">Document Title *</label>
                                <input
                                    type="text"
                                    value={uploadData.name}
                                    onChange={(e) => setUploadData({ ...uploadData, name: e.target.value })}
                                    placeholder="e.g. Complete Blood Count (CBC) Report"
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-1">Category</label>
                                <select
                                    value={uploadData.category}
                                    onChange={(e) => setUploadData({ ...uploadData, category: e.target.value })}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="Lab Report">Lab Report</option>
                                    <option value="Imaging / Scan">Imaging / Scan (X-Ray, MRI, CT)</option>
                                    <option value="Discharge Summary">Discharge Summary</option>
                                    <option value="Vaccination Record">Vaccination Record</option>
                                    <option value="Other">Other Document</option>
                                </select>
                            </div>

                            <div>
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-1">File Attachments * (Multiple Supported)</label>
                                <div className="border-2 border-dashed border-slate-200 hover:border-indigo-400 rounded-2xl p-5 text-center transition-all bg-slate-50/50">
                                    <input
                                        type="file"
                                        id="patient-file-input"
                                        multiple
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files.length > 0) {
                                                const newFiles = Array.from(e.target.files);
                                                setSelectedFiles(prev => [...prev, ...newFiles]);
                                            }
                                        }}
                                        className="hidden"
                                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                                    />
                                    <label htmlFor="patient-file-input" className="cursor-pointer block">
                                        <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-xl mx-auto mb-2">
                                            <i className="fa-solid fa-file-circle-plus"></i>
                                        </div>
                                        <p className="text-xs font-bold text-slate-800">
                                            {selectedFiles.length > 0 ? `Selected ${selectedFiles.length} file(s) • Click to add more` : "Click to select PDF or image files"}
                                        </p>
                                        <p className="text-[10px] text-slate-400 mt-1">PDF, JPG, PNG up to 25MB each</p>
                                    </label>
                                </div>

                                {selectedFiles.length > 0 && (
                                    <div className="mt-3 space-y-2 max-h-36 overflow-y-auto scrollbar-thin">
                                        {selectedFiles.map((file, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-2.5 bg-indigo-50/60 border border-indigo-100 rounded-xl text-xs">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <i className="fa-solid fa-paperclip text-indigo-600 shrink-0"></i>
                                                    <span className="font-bold text-slate-800 truncate" title={file.name}>{file.name}</span>
                                                    <span className="text-[10px] text-slate-400 font-medium shrink-0">({(file.size / 1024).toFixed(1)} KB)</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== idx))}
                                                    className="w-6 h-6 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 flex items-center justify-center shrink-0 ml-2"
                                                    title="Remove file"
                                                >
                                                    <i className="fa-solid fa-xmark text-xs"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-1">Personal Notes (Optional)</label>
                                <textarea
                                    value={uploadData.notes}
                                    onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
                                    rows={2}
                                    placeholder="Add any context or instructions..."
                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6 pt-4 border-t border-slate-100">
                            <button
                                onClick={() => setShowUploadModal(false)}
                                className="flex-1 py-3 rounded-2xl font-bold text-xs text-slate-600 hover:bg-slate-100 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handlePatientUpload}
                                disabled={isUploading}
                                className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all shadow-sm disabled:opacity-50 active:scale-95"
                            >
                                {isUploading ? "Uploading..." : "Save Document"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* PRE-CONSULTATION TRIAGE MODAL */}
            {triageAppointment && (
                <PreConsultationTriageModal
                    appointment={triageAppointment}
                    isOpen={!!triageAppointment}
                    onClose={() => setTriageAppointment(null)}
                />
            )}

            {/* DIGITAL PRESCRIPTION MODAL */}
            {viewingPrescriptionRecord && (
                <DigitalPrescriptionModal
                    record={viewingPrescriptionRecord}
                    onClose={() => setViewingPrescriptionRecord(null)}
                />
            )}

            {/* MEDICAL RECORD & DOCUMENT METADATA MODAL */}
            {viewingDoc && (
                <MedicalRecordMetadataModal
                    record={viewingDoc}
                    isOpen={!!viewingDoc}
                    onClose={() => setViewingDoc(null)}
                    onDelete={deleteMedicalRecord}
                    onOpenPrescriptionModal={(rec) => {
                        setViewingDoc(null);
                        setViewingPrescriptionRecord(rec);
                    }}
                />
            )}

            {/* DOCTOR REVIEW MODAL */}
            {reviewModalOpen && reviewTarget && (
                <div className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-100 animate-in zoom-in-95 duration-200">
                        <div className="text-center">
                            <div className="w-14 h-14 bg-amber-50 text-amber-500 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-3">
                                <i className="fa-solid fa-star"></i>
                            </div>
                            <h4 className="font-black text-slate-900 text-lg">Rate Your Consultation</h4>
                            <p className="text-xs text-slate-500 font-medium">How was your session with {reviewTarget.name}?</p>
                        </div>

                        <div className="flex justify-center gap-2 my-6">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    onClick={() => setReviewData({ ...reviewData, rating: star })}
                                    className={`text-2xl transition-all ${star <= reviewData.rating ? 'text-amber-400 scale-110' : 'text-slate-200 hover:text-amber-200'}`}
                                >
                                    <i className="fa-solid fa-star"></i>
                                </button>
                            ))}
                        </div>

                        <div>
                            <label className="text-[11px] font-black uppercase tracking-wider text-slate-400 block mb-1">Your Review & Comments</label>
                            <textarea
                                value={reviewData.comment}
                                onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                                rows={3}
                                placeholder="Share details about doctor attentiveness, explanation clarity, or care advice..."
                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                            />
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setReviewModalOpen(false)}
                                className="flex-1 py-3 rounded-2xl font-bold text-xs text-slate-600 hover:bg-slate-100 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmitReview}
                                className="flex-1 py-3 bg-amber-500 hover:bg-amber-600 text-white font-black text-xs uppercase tracking-wider rounded-2xl transition-all shadow-sm active:scale-95"
                            >
                                Submit Review
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
